<template>
  <v-sheet elevation="3">
    <v-form>
      <v-container fluid>
        <h1 class="headline mb-3">
          Bienvenue sur la plateforme Home in Love !
        </h1>
        <p class="body-1">
          Vous avez un projet de recherche de logement ? À la location ou à
          l’achat ?
        </p>
        <p>
          Home in Love vous accompagne dans votre projet et à la recherche de
          votre futur logement.
        </p>
        <p>
          Référencé par Action Logement, nous vous aiguillerons aussi sur tous
          les dispositifs d’aides financières (location et achat) dont vous
          pourrez bénéficier.
        </p>
        <p>Un parcours en {{ steps.length }} étapes :</p>
        <v-layout tag="ol" row align-start wrap class="reset">
          <Step
            v-for="(step, i) in steps"
            :key="i"
            :src="step.src"
            :title="step.title"
          />
        </v-layout>
        <p>
          Vivez sereinement votre projet de mobilité, nous nous occupons de tout
          !
        </p>
        <v-layout row wrap>
          <h2 class="mb-3 title">Inscription</h2>
          <v-flex xs12>
            <v-text-field
              v-model="email"
              outline
              v-validate="{
                required: true,
                email: {
                  allow_utf8_local_part: false
                }
              }"
              data-vv-name="email"
              :error-messages="errors.collect('email')"
            >
              <template v-slot:label>
                <span>Votre adresse mail&nbsp;</span>
                <span class="red--text">*</span>
              </template>
            </v-text-field>
          </v-flex>
          <v-flex xs12 md6>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="newPassword"
                  outline
                  type="password"
                  ref="new1"
                  v-validate="{
                    required: true,
                    min: 8,
                    regex: /^(?=.*[A-z])(?=.*[A-Z])(?=.*[0-9])\S{8,}$/
                  }"
                  data-vv-name="new1"
                  :error-messages="errors.collect('new1')"
                >
                  <template v-slot:label>
                    <span>Mot de passe&nbsp;</span>
                    <span class="red--text">*</span>
                    <span>&nbsp;(minuscules, majuscules et chiffres)</span>
                  </template>
                  <template v-slot:append>
                    <v-icon slot="activator" dark color="primary" v-on="on"
                      >info</v-icon
                    >
                  </template>
                </v-text-field>
              </template>
              <span class="password">
                <p>Votre mot de passe doit comporter au moins :</p>
                <ul>
                  <li>8 caractères</li>
                  <li>Une majuscule</li>
                  <li>Une minuscule</li>
                  <li>Un chiffre</li>
                </ul>
              </span>
            </v-tooltip>
          </v-flex>
          <v-flex xs12 md6>
            <v-text-field
              v-model="newPasswordBis"
              outline
              type="password"
              v-validate="'required|confirmed:new1'"
              data-vv-name="new2"
              :error-messages="errors.collect('new2')"
            >
              <template v-slot:label>
                <span>Confirmez le mot de passe&nbsp;</span>
                <span class="red--text">*</span>
              </template>
            </v-text-field>
          </v-flex>
          <v-flex xs12>
            <v-text-field
              v-model="phone"
              outline
              v-validate="'required|min:10'"
              data-vv-name="phone"
              :error-messages="errors.collect('phone')"
            >
              <template v-slot:label>
                <span>Numéro de téléphone&nbsp;</span
                ><span class="red--text">*</span
                ><span>&nbsp;(afin que nous puissions vous rappeler)</span>
              </template>
            </v-text-field>
          </v-flex>
          <v-flex xs12>
            <v-text-field
              v-model="origin"
              outline
              label="Comment nous avez-vous connu ?"
            />
          </v-flex>
          <v-flex xs12 class="px-2">
            <v-checkbox
              style="margin-top: 0"
              v-model="accepted"
              v-validate="'required:true'"
            >
              <template v-slot:label>
                <span>J'ai lu et j'accepte les&nbsp;</span>
                <a
                  target="_blank"
                  :href="`${apiUrl}/assets/cgu.pdf`"
                  @click.stop
                >
                  conditions générales
                </a>
              </template>
            </v-checkbox>
          </v-flex>
          <v-flex xs12 class="px-2">
            <v-checkbox
              style="margin-top: 0"
              v-model="newsletter"
              label="Je souhaite recevoir la newsletter d'Home in Love"
            ></v-checkbox>
          </v-flex>
          <v-flex xs12 class="d-flex d-flex-initial justify-center">
            <v-btn
              outline
              @click="submit"
              color="accent"
              :disabled="isFormInvalid"
              >Valider l'inscription</v-btn
            >
          </v-flex>
          <v-flex xs12 style="text-align: center">
            Déjà inscrit ? <a @click="$router.push('/')">Connectez-vous</a> !
          </v-flex>
        </v-layout>
      </v-container>
      <v-snackbar v-model="showError" :bottom="true">
        Une erreur a eu lieu - Vérifiez que votre mot de passe est conforme.
      </v-snackbar>
      <v-snackbar v-model="showLeaseCreateError" :bottom="true">
        Une erreur a eu lieu - Veuillez nous contacter à l'adresse e-mail.
      </v-snackbar>
      <v-snackbar v-model="showFormError" :bottom="true">
        Merci de bien saisir tous les champs
      </v-snackbar>
    </v-form>
  </v-sheet>
</template>

<script>
import { API_URL } from "../../../webapp-commons/config";
import { fetchHil } from "@/utils";
import Step from "@/components/commons/Step";
import { STEPS } from "@/data/DefaultSettings";

export default {
  name: "SignupForm",
  components: { Step },
  data: function() {
    return {
      apiUrl: API_URL,
      email: "",
      newPassword: "",
      newPasswordBis: "",
      phone: "",
      origin: "",
      showError: false,
      showLeaseCreateError: false,
      accepted: false,
      newsletter: false,
      showFormError: false,
      isFormComplete: false,
      steps: STEPS
    };
  },
  computed: {
    isFormInvalid: function() {
      return (
        this.errors.count() > 0 ||
        !Object.keys(this.fields).every(key => this.fields[key].validated)
      );
    },
    saleTrigram() {
      return sessionStorage.getItem("referer") || "";
    },
    refererCompany() {
      const id = localStorage.getItem("companyReferer");
      if (!id) {
        return null;
      }
      return {
        id,
        name: localStorage.getItem("companyRefererName") || ""
      };
    }
  },
  methods: {
    async submit() {
      if (!(await this.$validator.validateAll()) || !this.accepted) {
        this.showFormError = true;
        return;
      }
      try {
        const registerResponse = await fetchHil("/register", {
          method: "POST",
          body: {
            email: this.email,
            password: this.newPassword,
            phone: this.phone,
            origin: this.origin,
            newsletter: this.newsletter
          }
        });
        if (!registerResponse.ok) {
          if ((await registerResponse.json()).message === "duplicateMail") {
            this.errors.add({
              field: "email",
              msg: "Cet email existe déjà"
            });
            return;
          } else {
            throw new Error("resp has failed");
          }
        }
        const { token } = await registerResponse.json();
        if (window.ReactNativeWebView != null) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              site: "CLIENT",
              type: "LOGIN",
              token
            })
          );
        }
        localStorage.setItem("hil_token", token);

        const leaseResponse = await fetchHil("/lease", {
          method: "POST",
          body: {
            phoneNumber: this.phone,
            assignees: {
              sale: this.saleTrigram
            },
            sponsor: this.origin,
            school: this.refererCompany?.name,
            prescriberId: this.refererCompany?.id
          }
        });

        if (!leaseResponse.ok) {
          this.showLeaseCreateError = true;
          return;
        }

        const lease = await leaseResponse.json();
        this.$router.push(`/leases/project/${lease._id}`);
      } catch (e) {
        console.warn(e);
        this.showError = true;
      }
    }
  }
};
</script>

<style scoped>
.password {
  font-style: italic;
  font-size: small;
}
</style>
